import {
  Box,
  Chip,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { CarrierVisitStatus, RailVisitResponseDto } from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { RailcarAutocomplete } from '@planning/components'
import { CustomDateTimePicker } from '@planning/components/CustomDateTimePicker'
import IconTruck from '@planning/components/icon/IconTruck'
import { RailVisitAutoComplete } from '@planning/components/visit/RailVisitAutoComplete'
import { VesselVisitAutoComplete } from '@planning/components/visit/VesselVisitAutoComplete'
import RailTrackAutocomplete from '@planning/pages/Order/components/RailTrackAutocomplete'
import { useTranslate } from '@tolgee/react'
import { IFormStore, useRegisterSubFormStore } from '@tom-ui/utils'
import dayjs, { Dayjs } from 'dayjs'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { FC, useEffect } from 'react'
import {
  ChangeTransportSubFormStore,
  ChangeTransportSubFormType,
} from './ChangeTransportSubFormStore'

export const RegisteredChangeTransportSubForm = observer(
  <T,>({
    formStore,
    name,
    initialValues,
    hideContainerReleasePrompt,
    disableUsedRailcarFiltering,
    hideUniversalOption,
  }: {
    formStore: IFormStore<T>
    name: keyof T
    initialValues?: Partial<ChangeTransportSubFormType>
    hideContainerReleasePrompt?: boolean
    disableUsedRailcarFiltering?: boolean
    hideUniversalOption?: boolean
  }) => {
    const store = useRegisterSubFormStore(
      formStore,
      name,
      new ChangeTransportSubFormStore(initialValues),
    )

    return (
      <ChangeTransportSubForm
        store={store}
        hideContainerReleasePrompt={hideContainerReleasePrompt}
        disableUsedRailcarFiltering={disableUsedRailcarFiltering}
        hideUniversalOption={hideUniversalOption}
      />
    )
  },
)

export interface IChangeTransportSubFormProps {
  store: ChangeTransportSubFormStore
  hideContainerReleasePrompt?: boolean
  disableUsedRailcarFiltering?: boolean
  isSmallView?: boolean
  disabled?: boolean
  hideUniversalOption?: boolean
}

export const ChangeTransportSubForm: FC<IChangeTransportSubFormProps> = observer(
  ({ store, hideContainerReleasePrompt, isSmallView, disabled, hideUniversalOption }) => {
    const { t } = useTranslate()
    const { railcarTrackPositionItemStore, railTrackItemStore, tenantStore } = usePlanningStore()

    useEffect(() => {
      railTrackItemStore.fetch()
    }, [railTrackItemStore])

    const fetchRailcarTrackPositions = (visit?: RailVisitResponseDto) => {
      if (visit?.railcarTrackPositionIds?.length) {
        railcarTrackPositionItemStore.fetchByIds(visit.railcarTrackPositionIds)
      }
    }

    const getRailTracksByVisit = (visitId?: number) => {
      const railTracks = railcarTrackPositionItemStore.getRailTracks()
      if (visitId) {
        const railTrackIds = store.selectedRailVisit?.assignedRailTrackIds || []
        return railTracks.filter(x => railTrackIds.includes(x.id))
      }
      return railTracks
    }

    const railTracks = computed(() => getRailTracksByVisit(store.carrierVisitId.value)).get()

    const carrierVisitGridSize = {
      xs: 9,
      md: isSmallView ? null : 4.5,
      lg: isSmallView ? null : 6,
    }

    const barotiGridSize = {
      xs: 4,
      md: isSmallView ? null : 1.5,
      lg: isSmallView ? null : 1,
    }

    return (
      <Grid container spacing={2}>
        <Grid size={3}>
          <FormControl fullWidth error={store.carrierType.isTouched && !!store.carrierType.error}>
            <InputLabel id='carrier-select'>{t('carrier', 'Carrier')}</InputLabel>
            <Select
              labelId='carrier-select'
              label={t('carrier', 'Carrier')}
              value={store.carrierType.value}
              onChange={e => store.setCarrierType(e.target.value as string)}
              data-cy='change-transport-select'
              disabled={disabled}
            >
              {!hideUniversalOption && (
                <MenuItem value='Universal'>{t('unknown', 'Unknown')}</MenuItem>
              )}
              <MenuItem value='Train' data-cy='Train-option'>
                {t('train', 'Train')}
              </MenuItem>
              <MenuItem value='Truck' data-cy='Truck-option'>
                {t('truck', 'Truck')}
              </MenuItem>
              <MenuItem value='Vessel' data-cy='Vessel-option'>
                {t('vessel', 'Vessel')}
              </MenuItem>
            </Select>
            {store.carrierType.isTouched && store.carrierType.error && (
              <FormHelperText error>
                {t(store.carrierType.error, store.carrierType.error)}
              </FormHelperText>
            )}
          </FormControl>
        </Grid>
        {store.carrierType.value === 'Train' && (
          <>
            <Grid size={3}>
              <RailVisitAutoComplete
                selectedRailVisit={store.selectedRailVisit}
                error={store.carrierVisitId.isTouched && !!store.carrierVisitId.error}
                disabled={disabled}
                handleOnChange={visit => {
                  store.setSelectedRailVisit(visit)
                  fetchRailcarTrackPositions(visit)
                  store.carrierVisitId.setValue(visit?.id ?? 0)
                }}
                statuses={[
                  CarrierVisitStatus.Expected,
                  CarrierVisitStatus.InOperation,
                  CarrierVisitStatus.Arrived,
                ]}
                cargoType='Container'
              />
            </Grid>

            <Grid size={3}>
              <RailTrackAutocomplete
                label='Rail Track'
                hasError={store.railTrackId.isTouched && !!store.railTrackId.error}
                value={store.railTrackId.value}
                options={railTracks}
                disabled={disabled}
                onChange={id => store.railTrackId.setValue(id ?? '')}
              />
            </Grid>

            <Grid size={3}>
              <RailcarAutocomplete
                hasError={store.railcarId.isTouched && !!store.railcarId.error}
                label={'Railcar no.'}
                value={store.railcarId.value}
                disabled={disabled}
                onChange={id => store.railcarId.setValue(id)}
                required
              />
            </Grid>
          </>
        )}
        {store.carrierType.value === 'Vessel' && (
          <>
            <Grid size={carrierVisitGridSize}>
              <VesselVisitAutoComplete
                selectedVesselVisit={store.selectedVesselVisit}
                error={store.carrierVisitId.isTouched && !!store.carrierVisitId.error}
                handleOnChange={visit => {
                  store.setSelectedVesselVisit(visit)
                  store.carrierVisitId.setValue(visit?.id ?? 0)
                }}
                cargoType='Container'
                disabled={disabled}
                hideBlockedForEdit
              />
            </Grid>
            <Grid size={barotiGridSize}>
              <TextField
                {...store.bay.props}
                fullWidth
                label={`${t('bay', 'Bay')}`}
                disabled={disabled}
                onChange={event => {
                  const digitsOnly = event.target.value.replace(/\D/g, '').slice(0, 3)
                  store.bay.setValue(digitsOnly)
                }}
                helperText={store.bay.error ? t('fieldIsRequired', 'Field is required.') : ''}
                data-cy='bay-input'
              />
            </Grid>
            <Grid size={barotiGridSize}>
              <TextField
                {...store.row.props}
                fullWidth
                label={`${t('row', 'Row')}`}
                disabled={disabled}
                onChange={event => {
                  const digitsOnly = event.target.value.replace(/\D/g, '').slice(0, 3)
                  store.row.setValue(digitsOnly)
                }}
                helperText={store.row.error ? t('fieldIsRequired', 'Field is required.') : ''}
                data-cy='row-input'
              />
            </Grid>
            <Grid size={barotiGridSize}>
              <TextField
                {...store.tier.props}
                fullWidth
                label={`${t('tier', 'Tier')}`}
                disabled={disabled}
                onChange={event => {
                  const digitsOnly = event.target.value.replace(/\D/g, '').slice(0, 3)
                  store.tier.setValue(digitsOnly)
                }}
                helperText={store.tier.error ? t('fieldIsRequired', 'Field is required.') : ''}
                data-cy='tier-input'
              />
            </Grid>
          </>
        )}
        {store.carrierType.value === 'Truck' && (
          <>
            <Grid size={5}>
              <Box sx={{ display: 'flex', height: '100%', alignItems: 'center' }}>
                <IconTruck zoom={150} />
                <Typography variant='subtitle1' ml='.5rem'>
                  {t('truck', 'Truck')}
                </Typography>
              </Box>
            </Grid>
            {!(hideContainerReleasePrompt === true) && (
              <>
                {!tenantStore.skipReleaseOrder ? (
                  <Grid size={5}>
                    <Box sx={{ mt: '1rem' }}>
                      <Typography variant='subtitle2' mb={'.75rem'}>
                        {`${t('containerIsReleasedForPickUp', 'Container is released for pick-up')}?`}
                      </Typography>
                      <Chip
                        label={t('yes', 'Yes')}
                        disabled={disabled}
                        color='primary'
                        sx={{ padding: '0 1rem', mr: '.75rem' }}
                        variant={!store.showDatetimeForPickUp ? 'outlined' : undefined}
                        onClick={() => {
                          store.setShowDatetimeForPickUp(true)
                          store.releaseExpires.setValue(dayjs().toString())
                        }}
                      />
                      <Chip
                        label={t('no', 'No')}
                        disabled={disabled}
                        color='primary'
                        sx={{ padding: '0 1rem' }}
                        variant={store.showDatetimeForPickUp ? 'outlined' : undefined}
                        onClick={() => {
                          store.setShowDatetimeForPickUp(false)
                          store.releaseExpires.setValue('')
                        }}
                      />
                    </Box>

                    {store.showDatetimeForPickUp && (
                      <Box sx={{ mb: '1rem', mt: '1rem' }}>
                        <CustomDateTimePicker
                          label={t('releaseExpiresAfter', 'Release expires after')}
                          value={store.releaseExpires.value.toString()}
                          disabled={disabled}
                          onChange={(value: Dayjs | null) => {
                            store.releaseExpires.setValue((value ?? '').toString())
                          }}
                          popperPlacement={'top-end'}
                        />
                      </Box>
                    )}
                  </Grid>
                ) : (
                  ''
                )}
              </>
            )}
          </>
        )}
      </Grid>
    )
  },
)
